import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Ticket, LogOut, Plus, LayoutDashboard, Shield, Menu, X } from "lucide-react";
import { useState } from "react";

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const navLinkClass = ({ isActive }) =>
        `px-3 py-2 font-bold uppercase tracking-wider text-sm border-2 border-transparent hover:border-ink ${
            isActive ? "bg-mint border-ink" : ""
        }`;

    const onLogout = async () => {
        await logout();
        navigate("/");
    };

    return (
        <div className="min-h-screen flex flex-col bg-paper text-ink">
            <header className="border-b-2 border-ink bg-paper sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
                    <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
                        <div className="w-9 h-9 bg-coral border-2 border-ink shadow-brut-sm flex items-center justify-center -rotate-3">
                            <Ticket className="w-5 h-5 text-white" strokeWidth={3} />
                        </div>
                        <span className="font-display font-black text-2xl tracking-tight">RifaPerú</span>
                    </Link>

                    <nav className="hidden md:flex items-center gap-1">
                        <NavLink to="/" end className={navLinkClass} data-testid="nav-home">
                            Inicio
                        </NavLink>
                        <NavLink to="/rifas" className={navLinkClass} data-testid="nav-rifas">
                            Rifas
                        </NavLink>
                        {user && (
                            <NavLink to="/dashboard" className={navLinkClass} data-testid="nav-dashboard">
                                Mi panel
                            </NavLink>
                        )}
                        {user?.role === "admin" && (
                            <NavLink to="/admin" className={navLinkClass} data-testid="nav-admin">
                                Admin
                            </NavLink>
                        )}
                    </nav>

                    <div className="hidden md:flex items-center gap-2">
                        {user ? (
                            <>
                                <Link to="/crear-rifa" className="brut-btn brut-btn-primary text-xs" data-testid="cta-create-raffle">
                                    <Plus className="w-4 h-4" strokeWidth={3} /> Crear rifa
                                </Link>
                                <span className="text-xs uppercase tracking-widest font-bold">Hola, {user.name?.split(" ")[0]}</span>
                                <button onClick={onLogout} className="brut-btn text-xs" data-testid="logout-btn">
                                    <LogOut className="w-4 h-4" strokeWidth={3} />
                                </button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" className="brut-btn text-xs" data-testid="nav-login">Ingresar</Link>
                                <Link to="/register" className="brut-btn brut-btn-primary text-xs" data-testid="nav-register">Crear cuenta</Link>
                            </>
                        )}
                    </div>

                    <button
                        className="md:hidden brut-btn p-2"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Menú"
                    >
                        {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                    </button>
                </div>

                {open && (
                    <div className="md:hidden border-t-2 border-ink bg-paper p-4 flex flex-col gap-3">
                        <Link onClick={() => setOpen(false)} to="/" className="font-bold uppercase">Inicio</Link>
                        <Link onClick={() => setOpen(false)} to="/rifas" className="font-bold uppercase">Rifas</Link>
                        {user && <Link onClick={() => setOpen(false)} to="/dashboard" className="font-bold uppercase">Mi panel</Link>}
                        {user?.role === "admin" && <Link onClick={() => setOpen(false)} to="/admin" className="font-bold uppercase">Admin</Link>}
                        {user ? (
                            <>
                                <Link onClick={() => setOpen(false)} to="/crear-rifa" className="brut-btn brut-btn-primary text-xs">
                                    <Plus className="w-4 h-4" /> Crear rifa
                                </Link>
                                <button onClick={() => { setOpen(false); onLogout(); }} className="brut-btn text-xs">
                                    <LogOut className="w-4 h-4" /> Salir
                                </button>
                            </>
                        ) : (
                            <>
                                <Link onClick={() => setOpen(false)} to="/login" className="brut-btn text-xs">Ingresar</Link>
                                <Link onClick={() => setOpen(false)} to="/register" className="brut-btn brut-btn-primary text-xs">Crear cuenta</Link>
                            </>
                        )}
                    </div>
                )}
            </header>

            <main className="flex-1">{children}</main>

            <footer className="border-t-2 border-ink bg-ink text-paper mt-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 bg-coral border-2 border-paper flex items-center justify-center -rotate-3">
                                <Ticket className="w-4 h-4 text-white" strokeWidth={3} />
                            </div>
                            <span className="font-display font-black text-xl">RifaPerú</span>
                        </div>
                        <p className="text-sm opacity-70 max-w-sm">
                            La forma más rápida y transparente de crear y participar en rifas pagando con Yape.
                        </p>
                    </div>
                    <div className="text-xs uppercase tracking-widest opacity-60">
                        © {new Date().getFullYear()} RifaPerú · Hecho en Perú con cariño 🇵🇪
                    </div>
                </div>
            </footer>
        </div>
    );
}
